import type { SortingState } from "@tanstack/react-table"
import dayjs from "dayjs"

import {
  AP_AGING_DEFAULT_INVOICE_SORT_FIELD,
  AP_AGING_DEFAULT_INVOICE_SORT_ORDER,
  AP_AGING_ELIGIBLE_STATUSES,
  AP_AGING_SEARCH_MAX_LENGTH,
} from "./constants"
import {
  type ApAgingInvoice,
  type ApAgingInvoiceSort,
  ApAgingInvoiceSortField,
  type ApAgingTotals,
  type ApAgingVendor,
} from "./types/apAgingReport"
import type { ApAgingReportSearchParams } from "./types/searchParams"

import {
  type ApAgingReportInput,
  type GetApAgingReportQuery,
  InvoiceStatus,
  SortingOrder,
} from "@/graphql/codegen/graphql"
import { toUpperSnakeCase, upperSnakeCaseToCamelCase } from "@/utils/utils"

const DUE_DATE_CHIP_PREFIX = "Due Date: "

export const formatApAgingDateRangeChipLabel = (from: string | undefined, to: string | undefined): string => {
  if (from && to) return `${DUE_DATE_CHIP_PREFIX}${from} to ${to}`
  if (from) return `${DUE_DATE_CHIP_PREFIX}from ${from}`
  if (to) return `${DUE_DATE_CHIP_PREFIX}until ${to}`
  return ""
}

type ApAgingReportFilters = Pick<
  ApAgingReportSearchParams,
  "statuses" | "agingBuckets" | "dueDateFrom" | "dueDateTo" | "query"
>

// The filters of the report as the backend takes them, for the report and for the Excel export alike.
const toApAgingFilterInput = (
  searchParams: ApAgingReportFilters
): Pick<ApAgingReportInput, "statuses" | "agingBuckets" | "dueDateFrom" | "dueDateTo" | "query"> => ({
  // The backend reads an empty status list as no status at all, but on this screen an empty selection means every
  // eligible status, like an empty aging bucket selection.
  statuses: searchParams.statuses.length ? searchParams.statuses : [...AP_AGING_ELIGIBLE_STATUSES],
  agingBuckets: searchParams.agingBuckets,
  dueDateFrom: searchParams.dueDateFrom,
  dueDateTo: searchParams.dueDateTo,
  // The backend rejects a blank query and one longer than the limit: blank text means no search, long text is cut.
  query: searchParams.query?.trim().slice(0, AP_AGING_SEARCH_MAX_LENGTH) || undefined,
})

export const toApAgingReportInput = (
  searchParams: ApAgingReportFilters,
  limit: number,
  offset: number
): ApAgingReportInput => ({ ...toApAgingFilterInput(searchParams), offset, limit })

// AK-6964 AC10: The suggested file name, e.g. AP_Aging_Report_2026-10-13.xlsx.
export const getApAgingExportFileName = (date = dayjs()): string => `AP_Aging_Report_${date.format("YYYY-MM-DD")}.xlsx`

// AK-6964 AC10: The export reflects the filters currently applied (Status, Aging Bucket, Due Date Range and Vendor
// Search) and takes the on-screen invoice sort, so the file lists the invoices in the order of the screen. The lists
// are comma-separated, as the backend reads them.
export const toApAgingExportUrl = (
  baseUrl: string,
  searchParams: ApAgingReportFilters,
  invoiceSort: ApAgingInvoiceSort
): string => {
  const { statuses, agingBuckets, dueDateFrom, dueDateTo, query } = toApAgingFilterInput(searchParams)
  const params = new URLSearchParams()

  params.set("statuses", (statuses ?? []).join(","))
  if (agingBuckets?.length) params.set("agingBuckets", agingBuckets.join(","))
  if (dueDateFrom) params.set("dueDateFrom", dueDateFrom)
  if (dueDateTo) params.set("dueDateTo", dueDateTo)
  if (query) params.set("query", query)
  params.set("invoiceField", invoiceSort.field)
  params.set("invoiceOrder", invoiceSort.order)

  return `${baseUrl}/file/report/ap_aging?${params.toString()}`
}

// Status sorts in workflow order rather than alphabetically, as the backend sorts it (AK-7069 AC7), so the Excel export
// lists the invoices in the order of the screen. The statuses the report does not contain share the last place.
const STATUS_TO_SORT_RANK: Record<InvoiceStatus, number> = {
  [InvoiceStatus.Approved]: 0,
  [InvoiceStatus.PendingApproval]: 1,
  [InvoiceStatus.NotApproved]: 2,
  [InvoiceStatus.Paid]: 2,
  [InvoiceStatus.Cancelled]: 2,
  [InvoiceStatus.Voided]: 2,
}

const INVOICE_SORT_FIELD_TO_COMPARATOR: Record<
  ApAgingInvoiceSortField,
  (a: ApAgingInvoice, b: ApAgingInvoice) => number
> = {
  [ApAgingInvoiceSortField.DaysOverdue]: (a, b) => a.daysOverdue - b.daysOverdue,
  [ApAgingInvoiceSortField.DueDate]: (a, b) => a.dueDate.localeCompare(b.dueDate),
  [ApAgingInvoiceSortField.Status]: (a, b) => STATUS_TO_SORT_RANK[a.status] - STATUS_TO_SORT_RANK[b.status],
}

// AK-6964 AC7: The backend returns the invoices of a vendor ordered by due date, so the sort the user picks is applied
// here. Equal rows fall back to the due date, earliest first, then the invoice id, as the backend orders them, so the
// Excel export and the screen list them the same way.
export const sortApAgingInvoices = (
  invoices: ApAgingInvoice[],
  field: ApAgingInvoiceSortField,
  order: SortingOrder
): ApAgingInvoice[] => {
  const direction = order === SortingOrder.Desc ? -1 : 1
  const compare = INVOICE_SORT_FIELD_TO_COMPARATOR[field]

  const isDueDateSort = field === ApAgingInvoiceSortField.DueDate

  return [...invoices].sort(
    (a, b) =>
      direction * compare(a, b) ||
      (isDueDateSort ? 0 : a.dueDate.localeCompare(b.dueDate)) ||
      a.invoiceId.localeCompare(b.invoiceId)
  )
}

export const toApAgingTotals = ({
  current,
  days1To30,
  days31To60,
  days61To90,
  over90,
  total,
}: ApAgingTotals): ApAgingTotals => ({ current, days1To30, days31To60, days61To90, over90, total })

export type ApAgingReportResponse = Extract<
  GetApAgingReportQuery["financialReporting"]["apAgingReport"],
  { __typename: "ApAgingReportSuccess" }
>["report"]

export const toApAgingVendors = (vendors: ApAgingReportResponse["vendors"]): ApAgingVendor[] =>
  vendors.map((vendor) => ({
    vendorCompanyId: vendor.vendorCompanyId,
    vendorName: vendor.vendorCompany.name ?? "",
    vendorNumber: vendor.vendorCompany.publicId ?? "",
    as400VendorNumber: vendor.vendorCompany.externalSystemId ?? "",
    totals: toApAgingTotals(vendor.totals),
    invoices: vendor.invoices.map((invoice) => ({
      invoiceId: invoice.invoiceId,
      invoiceNumber: invoice.invoiceNumber,
      type: invoice.invoiceType,
      creationDate: invoice.creationDate,
      dueDate: invoice.dueDate,
      daysOverdue: invoice.daysOverdue,
      agingBucket: invoice.agingBucket,
      status: invoice.status,
      outstandingAmount: invoice.outstandingAmount,
    })),
  }))

export const toInvoiceSortingState = (field: ApAgingInvoiceSortField, order: SortingOrder): SortingState => [
  { id: upperSnakeCaseToCamelCase(field), desc: order === SortingOrder.Desc },
]

export const findInvoiceSortField = (columnId: string | undefined): ApAgingInvoiceSortField | undefined =>
  Object.values(ApAgingInvoiceSortField).find((sortField) => sortField === toUpperSnakeCase(columnId ?? ""))

// The invoice sort of the table in the shape the comparators and the export read; anything that cannot be sorted falls
// back to the default sort.
export const toApAgingInvoiceSort = (sorting: SortingState): ApAgingInvoiceSort => {
  const [sort] = sorting
  const field = findInvoiceSortField(sort?.id)

  if (!sort || !field) {
    return { field: AP_AGING_DEFAULT_INVOICE_SORT_FIELD, order: AP_AGING_DEFAULT_INVOICE_SORT_ORDER }
  }

  return { field, order: sort.desc ? SortingOrder.Desc : SortingOrder.Asc }
}
